// Инструменты MCP справочника — только на ввод (005: FR-028 – FR-032; ADR-0025, ADR-0026).
// Агент передаёт данные заказчика в сервис; в ответах — номера записей, вид, итог и счётчики.
// ФИО, почт и текста пунктов из справочника в ответах нет: у схем ответа нет свободных строк
// (проверка — test/mcp.test.ts). Сервер — дорожка S2, транспорт Streamable HTTP без состояния.
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import type { Operation } from '../rights/matrix.ts';
import { ALIAS_TEXT_MAX } from '../directory/alias.ts';
import {
  ExternalTypeSchema,
  ORGANIZATION_NAME_MAX,
  OrganizationSideSchema,
  PERSON_NAME_MAX,
  POSITION_MAX,
  RecordKindSchema,
} from '../directory/record.ts';

export const MCP_PATH = '/mcp';
export const MCP_MATCH_ROWS_MAX = 200;

/** Ключ идемпотентности изменяющего инструмента (ADR-0011): повтор с тем же ключом не меняет записи. */
const IdempotencyKeySchema = z.string().min(8).max(128).optional();

const text = (max: number) => z.string().trim().min(1).max(max);

/** Строка списка заказчика: ФИО или название, организация, должность, почта. */
export const MatchRowSchema = z
  .object({
    kind: RecordKindSchema.default('person'),
    fullName: text(PERSON_NAME_MAX).optional(),
    organization: text(ORGANIZATION_NAME_MAX).optional(),
    position: text(POSITION_MAX).optional(),
    email: text(254).optional(),
  })
  .refine((row) => (row.kind === 'person' ? !!row.fullName : !!row.organization));

export const MatchInputSchema = z.object({
  rows: z.array(MatchRowSchema).min(1).max(MCP_MATCH_ROWS_MAX),
  /** Юрлицо группы, в контексте которого сопоставлять однофамильцев (research, Р6). */
  legalEntityId: IdSchema.optional(),
});

/** found — одна запись; ambiguous — несколько, пара ушла в «К разбору»; none — нет. */
export const matchOutcomes = ['found', 'ambiguous', 'none'] as const;
export type MatchOutcome = (typeof matchOutcomes)[number];
export const MatchOutcomeSchema = z.enum(matchOutcomes);

export type MatchInput = z.output<typeof MatchInputSchema>;

export const MatchOutputSchema = z.object({
  rows: z.array(
    z.object({
      /** Номер строки во входе, с 0. */
      index: z.number().int().min(0),
      outcome: MatchOutcomeSchema,
      recordId: IdSchema.nullable(),
      kind: RecordKindSchema,
      /** Вопрос ушёл контролёру — без имён кандидатов в ответе. */
      queued: z.boolean(),
    }),
  ),
  counts: z.object({
    found: z.number().int().min(0),
    ambiguous: z.number().int().min(0),
    none: z.number().int().min(0),
  }),
});

export type MatchOutput = z.infer<typeof MatchOutputSchema>;

export const CreatePersonInputSchema = z.object({
  fullName: text(PERSON_NAME_MAX),
  organizationId: IdSchema.optional(),
  position: text(POSITION_MAX).optional(),
  idempotencyKey: IdempotencyKeySchema,
});

export const CreateOrganizationInputSchema = z.object({
  name: text(ORGANIZATION_NAME_MAX),
  side: OrganizationSideSchema,
  externalType: ExternalTypeSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});

export const UpdateRecordInputSchema = z.object({
  recordId: IdSchema,
  fullName: text(PERSON_NAME_MAX).optional(),
  name: text(ORGANIZATION_NAME_MAX).optional(),
  externalType: ExternalTypeSchema.optional(),
  /** Написание из списка заказчика — предложенным псевдонимом, подтверждает человек. */
  alias: text(ALIAS_TEXT_MAX).optional(),
  idempotencyKey: IdempotencyKeySchema,
});

export const SetEmailInputSchema = z.object({
  recordId: IdSchema,
  address: text(254),
  idempotencyKey: IdempotencyKeySchema,
});

export const AddWorkplaceInputSchema = z.object({
  personId: IdSchema,
  organizationId: IdSchema,
  position: text(POSITION_MAX).optional(),
  primary: z.boolean().optional(),
  idempotencyKey: IdempotencyKeySchema,
});

export const SetCuratorInputSchema = z.object({
  personId: IdSchema,
  organizationId: IdSchema,
  primary: z.boolean().optional(),
  idempotencyKey: IdempotencyKeySchema,
});

export const SetDocumentZoneInputSchema = z.object({
  documentId: IdSchema,
  legalEntityId: IdSchema,
  idempotencyKey: IdempotencyKeySchema,
});

export const SuggestPairInputSchema = z.object({
  aId: IdSchema,
  bId: IdSchema,
  idempotencyKey: IdempotencyKeySchema,
});

export type UpdateRecordInput = z.output<typeof UpdateRecordInputSchema>;

/** Итог изменяющего инструмента: записано или уже было (повтор по ключу идемпотентности). */
export const toolResults = ['ok', 'unchanged'] as const;
export const ToolResultSchema = z.enum(toolResults);

export const RecordOutputSchema = z.object({
  result: ToolResultSchema,
  recordId: IdSchema,
  kind: RecordKindSchema,
  /** Похожие записи уже есть — пары ушли контролёру (FR-028). */
  similarExists: z.number().int().min(0),
});

export const EmailOutputSchema = z.object({
  result: ToolResultSchema,
  recordId: IdSchema,
  /** Адрес вне доменов установки — «не подтверждён» до решения контролёра (FR-013). */
  external: z.boolean(),
  /** Адрес уже у другой записи — пара ушла контролёру (FR-015). */
  sameEmailQueued: z.boolean(),
});

export const LinkOutputSchema = z.object({
  result: ToolResultSchema,
  id: IdSchema,
});

export interface McpToolDefinition {
  readonly operation: Operation;
  /** Пояснение инструмента для агента. */
  readonly description: string;
  readonly input: z.ZodType;
  readonly output: z.ZodObject;
  readonly mutating: boolean;
}

export const mcpTools = {
  directory_match: {
    operation: 'directory.read',
    description:
      'Сопоставить строки списка со справочником. Ответ — номер записи и итог found, ambiguous или none; неоднозначное разберёт контролёр',
    input: MatchInputSchema,
    output: MatchOutputSchema,
    mutating: false,
  },
  directory_create_person: {
    operation: 'directory.write',
    description: 'Создать человека. Похожие записи уйдут контролёру парой «тот же?»',
    input: CreatePersonInputSchema,
    output: RecordOutputSchema,
    mutating: true,
  },
  directory_create_organization: {
    operation: 'directory.write',
    description: 'Создать организацию: своя (юрлицо группы) или внешняя с типом',
    input: CreateOrganizationInputSchema,
    output: RecordOutputSchema,
    mutating: true,
  },
  directory_update: {
    operation: 'directory.write',
    description:
      'Изменить ФИО, название или тип записи по номеру; написание — предложенным псевдонимом',
    input: UpdateRecordInputSchema,
    output: RecordOutputSchema,
    mutating: true,
  },
  directory_set_email: {
    operation: 'directory.write',
    description: 'Записать почту человеку по номеру записи. Внешний адрес подтверждает контролёр',
    input: SetEmailInputSchema,
    output: EmailOutputSchema,
    mutating: true,
  },
  directory_add_workplace: {
    operation: 'directory.write',
    description: 'Добавить место работы: организация и должность; основное — одно',
    input: AddWorkplaceInputSchema,
    output: LinkOutputSchema,
    mutating: true,
  },
  directory_set_curator: {
    operation: 'directory.write',
    description: 'Назначить нашего сотрудника куратором организации; основной — один',
    input: SetCuratorInputSchema,
    output: LinkOutputSchema,
    mutating: true,
  },
  directory_set_document_zone: {
    operation: 'directory.write',
    description: 'Задать документу зону ответственности — юрлицо группы',
    input: SetDocumentZoneInputSchema,
    output: LinkOutputSchema,
    mutating: true,
  },
  directory_suggest_pair: {
    operation: 'directory.write',
    description: 'Предложить контролёру пару «тот же?» по двум номерам записей',
    input: SuggestPairInputSchema,
    output: LinkOutputSchema,
    mutating: true,
  },
} as const satisfies Record<string, McpToolDefinition>;

export type McpToolName = keyof typeof mcpTools;
export const mcpToolNames = Object.keys(mcpTools) as McpToolName[];
